import { Box, Button } from "@mui/material";
import { applySpec, map, omit, prop } from "ramda";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { AutocompleteSelect, BareInputField } from "../../components/Forms";
import { RHForm } from "../../components/Forms/Form";
import { useLoadableOptions } from "../../components/Forms/hooks";
import { useMsg } from "../../components/Msg/Msg";
import { H2, P } from "../../components/Typography";
import {
  useAuth,
  useMyMutation,
  useMyQuery,
} from "../Authorization/AuthProvider";
import { formatName } from "../Coaches/coach.utils";
import { ControlsContainer } from "../Sessions/steps/Controls";
import { FormRow } from "./FormRow";
import { WHITE_BG } from "./Settings.page";
import { generalMessages } from "../../components/messages";

const FIELDS = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",

  company: "company",
  businessStrategy: "businessStrategy",
  manager: "manager",

  position: "position",
  aspiredCompetency: "aspiredCompetency",
  aspiredPosition: "aspiredPosition",

  // industry: "industry",
  // department: "department",
  // hrManager: "hrManager",
};

const DEFAULT_VALUES = {
  firstName: "",
  lastName: "",

  company: "",
  businessStrategy: "",
  manager: "",

  position: "",
  aspiredCompetency: "",
  aspiredPosition: "",
};

export const useUserManagersQuery = () => {
  return useMyQuery({
    queryKey: ["user-settings", "managers"],
    fetchDef: { url: "/api/latest/user-settings/managers" },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useUserSettingsQuery = (rest = {}) => {
  return useMyQuery({
    queryKey: ["user-settings"],
    fetchDef: {
      url: `/api/latest/user-settings`,
    },
    ...rest,
  });
};

export const UserProfileSettings = () => {
  const msg = useMsg();
  const generalMsg = useMsg({ dict: generalMessages });
  const { isHR } = useAuth();
  const READ_ONLY = useMemo(
    () => (isHR ? [FIELDS.company] : [FIELDS.company, FIELDS.businessStrategy]),
    [isHR],
  );

  const initialValuesQuery = useUserSettingsQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: DEFAULT_VALUES,
  });
  const saveMutation = useMyMutation({
    fetchDef: {
      url: "/api/latest/user-settings",
      method: "PUT",
      from: omit(READ_ONLY),
    },
    invalidate: [{ queryKey: ["user-settings"] }],
  });
  const managersProps = useLoadableOptions({
    query: useUserManagersQuery(),
    map: map(
      applySpec({
        value: prop("username"),
        label: formatName,
      }),
    ),
  });

  const form = useForm({
    values: initialValuesQuery.data,
  });

  const onError = (errors, e, ...rest) =>
    console.log("[ProfileSettings.onError]", { errors, e, rest });

  const isInitialized = initialValuesQuery.isFetched;
  const saveDisabled =
    saveMutation.isPending ||
    !!initialValuesQuery.error ||
    initialValuesQuery.isFetching ||
    !isInitialized;

  console.log("[UserProfileSettings.rndr]", {
    initialValuesQuery,
    saveMutation,
    form,
  });

  return (
    <RHForm form={form} onSubmit={saveMutation.mutateAsync}>
      <P sx={{ mb: -1 }}>{msg("settings.user-profile.perex")}</P>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <FormRow
          label={msg("settings.user-profile.field.name")}
          name={FIELDS.lastName}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.firstName}
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.firstName)}
            placeholder={msg(
              "settings.user-profile.field.firstName.placeholder",
            )}
            parametrizedValidate={[
              ["required"],
              ["notBlank"],
              ["minLength", { gteLength: 2 }],
            ]}
          />
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.lastName}
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.lastName)}
            placeholder={msg(
              "settings.user-profile.field.lastName.placeholder",
            )}
            parametrizedValidate={[
              ["required"],
              ["notBlank"],
              ["minLength", { gteLength: 2 }],
            ]}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.email")}
          name={FIELDS.email}
          dividerTop={false}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.email}
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.email)}
            placeholder={msg("settings.user-profile.field.email.placeholder")}
            parametrizedValidate={[["required"], ["email"]]}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.company")}
          name={FIELDS.company}
          dividerTop={false}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.company}
            fullWidth
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.company)}
            placeholder={msg("settings.user-profile.field.company.placeholder")}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.businessStrategy")}
          name={FIELDS.businessStrategy}
          dividerTop={false}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.businessStrategy}
            disabled={
              !isInitialized || READ_ONLY.includes(FIELDS.businessStrategy)
            }
            placeholder={msg(
              "settings.user-profile.field.businessStrategy.placeholder",
            )}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.manager")}
          name={FIELDS.manager}
          dividerTop={false}
        >
          <AutocompleteSelect
            isLoading={!isInitialized}
            sx={WHITE_BG}
            name={FIELDS.manager}
            enableIsOptionEqualToValue
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.manager)}
            placeholder={msg("settings.user-profile.field.manager.placeholder")}
            {...managersProps}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.position")}
          name={FIELDS.position}
          dividerTop={false}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.position}
            disabled={!isInitialized || READ_ONLY.includes(FIELDS.position)}
            placeholder={msg(
              "settings.user-profile.field.position.placeholder",
            )}
          />
        </FormRow>
        <FormRow
          label={msg("settings.user-profile.field.aspiredCompetency")}
          name={FIELDS.aspiredCompetency}
          dividerTop={false}
        >
          <BareInputField
            isLoading={!isInitialized}
            name={FIELDS.aspiredCompetency}
            disabled={
              !isInitialized || READ_ONLY.includes(FIELDS.aspiredCompetency)
            }
            placeholder={msg(
              "settings.user-profile.field.aspiredCompetency.placeholder",
            )}
          />
        </FormRow>
        {/* https://topleader.atlassian.net/browse/TOP-219 */}
        {/* <FormRow
          label={msg("settings.user-profile.field.aspiredPosition")}
          name={FIELDS.aspiredPosition}
          dividerTop={false}
        >
          <BareInputField
          isLoading={!isInitialized}
            name={FIELDS.aspiredPosition}
            disabled={!isInitialized||READ_ONLY.includes(FIELDS.aspiredPosition)}
            placeholder={msg(
              "settings.user-profile.field.aspiredPosition.placeholder"
            )}
          />
        </FormRow> */}
        <FormRow dividerTop={false}>
          <ControlsContainer>
            <Button variant="contained" type="submit" disabled={saveDisabled}>
              {msg("settings.user-profile.save")}
            </Button>
          </ControlsContainer>
        </FormRow>
      </Box>
    </RHForm>
  );
};
